import type { CalcKey } from './keys';

export const ro: Record<CalcKey, string> = {
  // Common controls
  'panel.params': 'Parametri',
  'panel.advanced': 'Avansat',
  'action.reset': 'Resetează',
  'action.randomise': 'Aleatorizează',
  'action.reset-view': 'Resetează vizualizarea',
  // App chrome
  'app.eyebrow': 'Matematică · Calcul',
  // Experiment selector
  'panel.experiment': 'Experiment',
  // Experiment labels
  'experiment.limit-lab': 'Laborator limite',
  'experiment.tangent-lab': 'Laborator tangente',
  'experiment.derivative-plotter': 'Plotter derivate',
  'experiment.applications': 'Aplicații',
  'experiment.riemann-lab': 'Laborator Riemann',
  'experiment.ftc-lab': 'Laborator TFC',
  'experiment.taylor-lab': 'Laborator Taylor',
  'experiment.slope-field-lab': 'Câmp de pante',
  // Limit presets
  'preset.limit.removable': 'Discontinuitate eliminabilă',
  'preset.limit.jump': 'Discontinuitate de salt',
  'preset.limit.infinite': 'Discontinuitate infinită',
  'preset.limit.oscillating': 'Discontinuitate oscilantă',
  'preset.limit.continuous': 'Continuă (control)',
  // Tangent presets
  'preset.tangent.parabola': 'Parabolă x²',
  'preset.tangent.sine': 'Sinus sin(x)',
  'preset.tangent.cubic': 'Cubică x³ − 3x',
  'preset.tangent.exponential': 'Exponențială eˣ',
  // Limit-lab panel labels
  'limit.panel.preset': 'Funcție',
  'limit.panel.approach': 'Abordare x',
  'limit.panel.epsilon': 'ε (epsilon)',
  'limit.panel.show-epsilon-delta': 'Afișează banda ε–δ',
  // Limit-lab readout labels
  'limit.readout.left': 'lim x→a⁻',
  'limit.readout.right': 'lim x→a⁺',
  'limit.readout.fa': 'f(a)',
  'limit.readout.kind': 'tip',
  // Limit-lab info / help
  'experiment.limit-lab.info':
    'Trage cursorul de-a lungul axei x pentru a sonda limita. Afișajul arată limitele unilaterale și clasifică discontinuitatea la punctul de abordare.',
  // Tangent-lab panel labels
  'tangent.panel.preset': 'Funcție',
  'tangent.panel.a': 'Punct a',
  'tangent.panel.h': 'Pas h',
  'tangent.panel.mode': 'Tip coeficient',
  // Tangent-lab mode values
  'tangent.mode.forward': 'Înainte',
  'tangent.mode.symmetric': 'Simetric',
  // Tangent-lab info / help
  'experiment.tangent-lab.info':
    'Trage P pentru a muta punctul de bază și Q pentru a schimba pasul h. Urmărește cum dreapta secantă se apropie de tangentă pe măsură ce h → 0.',
  // Derivative presets
  'preset.derivative.cubic': 'Cubică x³ − 3x',
  'preset.derivative.sine': 'Sinus sin(x)',
  'preset.derivative.parabola': 'Parabolă x²',
  'preset.derivative.bell': 'Curbă clopot e^(−x²)',
  // Application presets
  'preset.application.open-box': 'Volum cutie deschisă',
  'preset.application.rect-under-line': 'Dreptunghi sub o dreaptă',
  'preset.application.ladder': 'Scară glisantă',
  'preset.application.ripple': 'Undă circulară',
  // Derivative-plotter panel labels
  'derivative.panel.preset': 'Funcție',
  'derivative.panel.cursor-x': 'Cursor x',
  // Derivative-plotter info
  'experiment.derivative-plotter.info':
    'Trage cursorul pe axa x. Panoul de sus arată f cu un segment tangent; panoul de jos trasează f′ — punctul de legătură arată cum panta tangentei este egală cu valoarea derivatei. Modul avansat adaugă f″ și haşura concavității.',
  // Applications panel labels
  'applications.panel.preset': 'Scenariu',
  'applications.panel.param': 'Parametru',
  'applications.panel.mode': 'Mod',
  // Applications mode values
  'applications.mode.optimization': 'Optimizare',
  'applications.mode.related-rates': 'Rate corelate',
  // Applications info
  'experiment.applications.info':
    'Optimizare: trage parametrul pentru a vedea scenariul geometric și curba cantității — tangenta marcatorului devine orizontală la optim. Rate corelate: observă cum vectorii dx/dt și dy/dt se relaționează prin regula lanțului.',
  // Integral presets
  'preset.integral.polynomial': 'Polinom x²',
  'preset.integral.sine': 'Sinus sin(x)',
  'preset.integral.cosine': 'Cosinus cos(x)',
  'preset.integral.exponential': 'Exponențială eˣ',
  'preset.integral.linear': 'Liniară 2x + 1',
  'preset.integral.reciprocal': 'Inversă 1/x',
  // Riemann-lab panel labels
  'riemann.panel.preset': 'Funcție',
  'riemann.panel.n': 'Subintervale n',
  'riemann.panel.rule': 'Regulă',
  // Riemann-lab rule values
  'riemann.rule.left': 'Stânga',
  'riemann.rule.right': 'Dreapta',
  'riemann.rule.mid': 'Punct mediu',
  'riemann.rule.trap': 'Trapez',
  // Riemann-lab info / help
  'experiment.riemann-lab.info':
    'Ajustează n pentru a împărți [a, b] în mai multe dreptunghiuri și urmărește cum suma Riemann Σ converge la integrala exactă ∫. Afișajul arată suma cu semn, valoarea primitivei curate și eroarea |Σ − ∫|.',
  // FTC-lab panel labels
  'ftc.panel.preset': 'Funcție',
  'ftc.panel.x': 'Limita superioară x',
  'ftc.panel.show-second-ftc': 'Afișează TFC 2 (verificare numerică)',
  // FTC-lab info / help
  'experiment.ftc-lab.info':
    'Trage x pentru a acumula aria de sub f de la a la x. Panoul de sus haşurează aria ∫ₐˣ f; panoul de jos trasează A(x) = F(x) − F(a) cu un punct de legătură. Afișajul arată primitiva curată F și identitatea TFC A′(x) = f(x).',
  // Taylor presets
  'preset.taylor.exp': 'Exponențială eˣ',
  'preset.taylor.sin': 'Sinus sin(x)',
  'preset.taylor.cos': 'Cosinus cos(x)',
  'preset.taylor.ln1px': 'Logaritm ln(1+x)',
  'preset.taylor.geometric': 'Geometrică 1/(1−x)',
  'preset.taylor.atan': 'Arctangent atan(x)',
  // Taylor-lab panel labels
  'taylor.panel.preset': 'Funcție',
  'taylor.panel.degree': 'Grad n',
  'taylor.panel.center': 'Centru a',
  // Taylor-lab info / help
  'experiment.taylor-lab.info':
    'Trage punctul central pe axa x pentru a muta centrul de expansiune. Mărește gradul pentru a adăuga mai mulți termeni — urmărește cum Pₙ(x) se apropie de f lângă centru și diverge în afara intervalului de convergență umbrit. Afișajul arată polinomul exact și coeficienții săi.',
  // Slope-field presets
  'preset.slope-field.exponential-growth': 'Creștere exponențială y′ = y',
  'preset.slope-field.linear-x': 'Pantă liniară y′ = x',
  'preset.slope-field.circles': 'Cercuri concentrice y′ = −x/y',
  'preset.slope-field.linear-ode': 'EDO liniară y′ = x − y',
  // Slope-field-lab panel labels
  'slope-field.panel.preset': 'Ecuație dy/dx',
  'slope-field.panel.step-h': 'Pas h',
  'slope-field.panel.show-family': 'Afișează familia de soluții',
  // Slope-field-lab info / help
  'experiment.slope-field-lab.info':
    'Trage punctul de condiție inițială pentru a trasa o curbă soluție prin câmpul de pante. În modul avansat, micșorează h pentru a reduce eroarea Euler și a vedea familia de curbe soluție. Suprapunerea slabă arată soluția în formă închisă, unde există.',
};
